import { Grid, Typography } from "@mui/material";
import { MultiInput } from "components/FormControls/MultiInput/MultiInput";
import { MultiWithModal } from "components/FormControls/MultiWithModal";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { OLMenuButton } from "components/FormControls/OLMenuButton";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { FieldArray, useFormikContext } from "formik";
import { useMessage } from "hooks/intl";
import { kebabCase } from "lodash";
import React from "react";
import { ItemCriteriaParam } from "types/campaigns";
import { firstLetterToLowercase } from "utilities/strings";
import { useStyles } from "./styles";

interface Props {
  fieldName: string;
  ruleIndex: number;
  index: number;
}

export function ItemsCriteria({ fieldName, ruleIndex, index }: Props) {
  const classes = useStyles();
  const { message } = useMessage();
  const { setFieldValue } = useFormikContext();

  const criteria = [
    { label: "productBrand", id: "productBrand" },
    { label: "productCategory", id: "productCategory" },
    { label: "productCustomAttribute", id: "productCustomAttribute" },
    { label: "productName", id: "productName" },
  ];

  return (
    <>
      <Grid item xs={6}>
        <SelectInput
          className={classes.input}
          width="100%"
          data-test-id="condition-operator"
          items={[
            { id: "is_equal", label: message({ id: "is_equal" }) },
            {
              id: "is_not_equal",
              label: message({ id: "is_not_equal" }),
            },
            { id: "is_greater", label: message({ id: "is_greater_than" }) },
            {
              id: "is_greater_or_equal",
              label: message({ id: "is_greater_or_equal" }),
            },
            { id: "is_less", label: message({ id: "is_less_than" }) },
            {
              id: "is_less_or_equal",
              label: message({ id: "is_less_or_equal" }),
            },
          ]}
          label={<RequiredFieldLabel text={message({ id: "condition" })} />}
          name={`${fieldName}.operator`}
          onChange={() => setFieldValue(`${fieldName}.data`, null)}
        />
      </Grid>
      <Grid item xs={12}>
        <NumberFormatInput
          allowDecimals
          label={
            <RequiredFieldLabel
              text={message({
                id: "value",
              })}
            />
          }
          name={`${fieldName}.data`}
          inputProps={{ step: "any" }}
          data-test-id="condition-value"
        />
      </Grid>
      <Grid item xs={12}>
        <Typography className={classes.textOperator}>
          {message({ id: "where" }).toUpperCase()}
        </Typography>
      </Grid>
      <FieldArray
        name={`${fieldName}.param`}
        render={({ push, remove, form: { values } }) => {
          const addedCriteria: ItemCriteriaParam[] =
            values.rules[ruleIndex].conditions[index].draft.param;
          const areCriteriaAdded = addedCriteria && addedCriteria.length > 0;

          return (
            <>
              {areCriteriaAdded && (
                <>
                  {addedCriteria.map((item, criterionIndex) => {
                    const isAndOperatorVisible =
                      criterionIndex + 1 < addedCriteria.length;

                    switch (item.criterion) {
                      case "productBrand":
                      case "productName":
                      case "productCategory":
                        return (
                          <React.Fragment key={criterionIndex}>
                            <Grid item xs={12} container columnSpacing={12}>
                              <Grid item xs={11}>
                                <MultiInput
                                  label={
                                    <RequiredFieldLabel
                                      text={message({
                                        id: firstLetterToLowercase(
                                          item.criterion.replace("product", ""),
                                        ),
                                      })}
                                    />
                                  }
                                  className={classes.input}
                                  formField={`${fieldName}.param.[${criterionIndex}].values`}
                                  placeholder={message({
                                    id: "multiInputHelper",
                                  })}
                                  data-test-id={kebabCase(item.criterion)}
                                />
                              </Grid>
                              <Grid
                                item
                                xs={1}
                                className={classes.deleteWrapper}
                                onClick={() => remove(criterionIndex)}
                              >
                                <XCloseIcon />
                              </Grid>
                            </Grid>
                            {isAndOperatorVisible && (
                              <Grid item xs={12}>
                                <Typography className={classes.textOperator}>
                                  {message({ id: "and" }).toUpperCase()}
                                </Typography>
                              </Grid>
                            )}
                          </React.Fragment>
                        );
                      case "productCustomAttribute":
                        return (
                          <React.Fragment key={criterionIndex}>
                            <Grid item xs={12} container columnSpacing={12}>
                              <Grid item xs={11}>
                                <MultiWithModal
                                  maxWidth="unset"
                                  className={classes.input}
                                  data-test-id="custom-attributes"
                                  formField={`${fieldName}.param.[${criterionIndex}].values`}
                                  label={
                                    <RequiredFieldLabel
                                      text={message({
                                        id: "customAttribute",
                                      })}
                                    />
                                  }
                                  placeholder={message({
                                    id: "addCustomAttribute",
                                  })}
                                />
                              </Grid>
                              <Grid
                                item
                                xs={1}
                                className={classes.deleteWrapper}
                                onClick={() => remove(criterionIndex)}
                              >
                                <XCloseIcon />
                              </Grid>
                            </Grid>
                            {isAndOperatorVisible && (
                              <Grid item xs={12}>
                                <Typography className={classes.textOperator}>
                                  {message({ id: "and" }).toUpperCase()}
                                </Typography>
                              </Grid>
                            )}
                          </React.Fragment>
                        );
                      default:
                        return null;
                    }
                  })}
                </>
              )}
              <Grid item xs={12}>
                <OLMenuButton
                  buttonVariant="outlined"
                  size="small"
                  startIcon={<PlusIcon />}
                  data-test-id="add-criteria"
                  items={criteria.map((item) => ({
                    label: message({ id: item.label }),
                    onClick: () => {
                      push({ criterion: item.id, values: [] });
                    },
                  }))}
                  submit={message({
                    id: "addCriteria",
                  })}
                />
              </Grid>
            </>
          );
        }}
      />
    </>
  );
}
